import React, { useMemo } from "react";
import { useFirestoreCollection } from "../../../lib/useFirestore.js";
import { Crumbs, Back } from "./nav.jsx";
import { pdfPageCount, includedCaptureCounts } from "../lib/pagecount.js";

/**
 * Screen 3 (v1) — "Which group?" Each rig group (Bigrig / Headrig) confirms
 * separately. "Proof & send" stays LOCKED until every group is confirmed — the
 * desktop tool's confirmation gate (design package §1a: no export, no push
 * until every included group is confirmed). The strip at the bottom shows the
 * bridge uploading thumbnails from the studio machine.
 */
export default function Groups({ nav, go }) {
  const { jobId, jobLabel } = nav;
  const { data: groups, loading } = useFirestoreCollection(jobId ? `jobs/${jobId}/captureSheet` : null);

  const sorted = useMemo(() => [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [groups]);
  const allConfirmed = sorted.length > 0 && sorted.every((g) => g.isConfirmed);
  const totalCaps = sorted.reduce((n, g) => n + (g.captureCount || 0), 0);
  const totalThumbs = sorted.reduce((n, g) => n + (g.thumbsUploaded || 0), 0);
  // Page estimate over the builder's EXACT inclusion set (confirmed groups,
  // captures with picks) — C4 parity with functions-py/main.py.
  const pageEstimate = useMemo(() => pdfPageCount(includedCaptureCounts(sorted)), [sorted]);

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sheets", screen: "home", patch: { jobId: null } },
          { label: jobLabel, screen: "job" },
          { label: "Capture Sheet" },
        ]}
        go={go}
      />
      <Back label={jobLabel} onClick={() => go("job")} />
      <div className="qhead">Which group?<span className="d">{sorted.length} rig groups · {totalCaps} captures</span></div>

      {loading ? (
        <div className="empty">Loading groups…</div>
      ) : (
        <>
          {sorted.map((g) => {
            const confirmed = !!g.isConfirmed;
            const sub = confirmed
              ? `${g.captureCount} captures · picks applied to all`
              : `${g.captureCount} captures · no picks yet`;
            return (
              <button key={g.id} type="button" className="row" onClick={() => go("picker", { groupId: g.id })}>
                <span className="num">⬛</span>
                <span className="grow">{g.label}<br /><span className="sub2">{sub}</span></span>
                <span className={`chip ${confirmed ? "green" : "amber"}`}>{confirmed ? "confirmed" : "needs picking"}</span>
                <span className={confirmed ? "btnS" : "btnP"} style={{ padding: "4px 10px", fontSize: 11 }}>
                  {confirmed ? "Edit picks" : "Pick cameras"}
                </span>
              </button>
            );
          })}

          <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
            <span style={{ color: "var(--s-muted)" }}>
              {allConfirmed ? `All groups confirmed — proof it before it goes anywhere · ≈ ${pageEstimate} pages`
                : "Proof & send unlocks when every group is confirmed"}
            </span>
            <button type="button" className="btnP" disabled={!allConfirmed} onClick={() => go("proof")}>
              Proof &amp; send →
            </button>
          </div>

          <div className="machstrip">
            <span className="dot" /> NYCAP-RIG · thumbnails {totalThumbs}/{totalCaps} captures uploaded
          </div>
        </>
      )}
    </>
  );
}
